import * as React from 'react'
import { useContext } from 'react'
import { redirect } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { EnvironmentContext } from 'config/environment'
import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCrumbDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

const RootPage = React.lazy(() => import('modules/competencies/pages/RootPage'))
const CompetenciesListPage = React.lazy(() => import('modules/competencies/pages/CompetenciesList'))
const CompetenciesGroupsPage = React.lazy(
  () => import('modules/competencies/pages/CompetencyGroups')
)
const AssessmentsPage = React.lazy(() => import('modules/competencies/pages/Assessments'))
const NineBoxPage = React.lazy(() => import('modules/competencies/pages/NineBox'))
const CompetencySettingsPage = React.lazy(
  () => import('modules/competencies/pages/CompetencySettings')
)

export const competenciesRoutes = buildNavigation((navigation) => {
  const { hideCompetenciesStandAlonePages } = useContext(EnvironmentContext)

  return [
    {
      // TODO: this replace is used until we move all the navigation items
      // of the modules under performance_v2 to the performance_v2 navigation
      path: navigation.competencies.path.replace(`${navigation.performanceProcess.path}/`, ''),
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: CompetenciesCrumbDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const isCompetenciesRevampEnabled = data.features.featuresConnection.nodes.some(
              (feature) => feature.name === 'dev_competencies_revamp'
            )

            return {
              title: i18n.t('competencies_v3.title'),
              illustration: 'window-user',
              description: i18n.t('competencies_v3.description'),
              subNavigation: [
                {
                  index: true,
                  title: i18n.t('competencies_v3.main_tabs.company_competencies'),
                  href: navigation.competencies.fullPath(),
                },
                {
                  title: i18n.t('competencies_v3.main_tabs.groups'),
                  href: navigation.competencies.groups.fullPath(),
                },
                {
                  title: i18n.t('competencies_v3.main_tabs.assessments'),
                  href: navigation.competencies.assessments.fullPath(),
                  isVisible: !isCompetenciesRevampEnabled && !hideCompetenciesStandAlonePages,
                },
                {
                  title: i18n.t('competencies_v3.main_tabs.ninebox'),
                  href: navigation.competencies.ninebox.fullPath(),
                  isVisible: !isCompetenciesRevampEnabled && !hideCompetenciesStandAlonePages,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <CompetenciesListPage />,
        },
        {
          path: navigation.competencies.groups.path,
          element: <CompetenciesGroupsPage />,
        },
        {
          path: navigation.competencies.assessments.path,
          element: <AssessmentsPage />,
        },
        {
          path: navigation.competencies.ninebox.path,
          element: <NineBoxPage />,
        },
        {
          path: navigation.competencies.competencyId.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              if (!params['competencyId']) {
                return { title: '' }
              }

              return watchQuery({
                query: CompetenciesCompetencyCrumbDocument,
                variables: {
                  id: Number(params['competencyId']),
                },
              }).map(({ data }) => {
                const title = data.competencies.competency?.name || ''

                if (!title) {
                  return { title: '' }
                }

                return {
                  title: title,
                  headerComponent: (
                    <ResourceHeader title={i18n.t('competencies_v3.settings.title')} />
                  ),
                }
              })
            },
          },
          children: [
            {
              index: true,
              loader: ({ params }) =>
                redirect(
                  navigation.competencies.competencyId.settings.fullPath({
                    competencyId: Number(params.competencyId),
                  })
                ),
            },
            {
              path: navigation.competencies.competencyId.settings.path,
              element: <CompetencySettingsPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('competencies_v3.settings.title'),
                }),
              },
            },
          ],
        },
      ],
    },
  ]
})
